<template>
  <f7-page name="admin-campaigns">
    <f7-navbar :title="$t('growth.campaigns.title')" back-link="Back" />
       <AppLayout>
  <template #main>
    <f7-toolbar bottom>
      <f7-link right @click="openCreateCampaign">
        <f7-icon f7="plus" slot="icon" class="t-green"></f7-icon>
        <span>{{ $t('growth.campaigns.newCampaign') }}</span>
      </f7-link>
    </f7-toolbar>

    <f7-block class="pb-0 growth-page">
      <!-- Overview Cards -->
      <f7-card class="growth-card mb-3">
        <f7-card-content>
          <div class="stats-row">
            <div class="stat text-align-center">
              <div class="t-h4 t-bold">{{ campaigns.length }}</div>
              <div class="t-muted t-small">{{ $t('growth.campaigns.total') }}</div>
            </div>
            <div class="stat text-align-center">
              <div class="t-h4 t-green t-bold">{{ activeCampaigns.length }}</div>
              <div class="t-muted t-small">{{ $t('growth.campaigns.active') }}</div>
            </div>
            <div class="stat text-align-center">
              <div class="t-h4 t-blue t-bold">{{ totalBudget }} <small>M</small></div>
              <div class="t-muted t-small">{{ $t('growth.campaigns.budget') }}</div>
            </div>
            <div class="stat text-align-center">
              <div class="t-h4 t-gold t-bold">{{ totalDistributed }} <small>M</small></div>
              <div class=" t-small">{{ $t('growth.campaigns.distributed') }}</div>
            </div>
          </div>
        </f7-card-content>
      </f7-card>

      <!-- Active Campaigns -->
      <f7-block-title class="t-bold">{{ $t('growth.campaigns.activeCampaigns') }}</f7-block-title>
      <f7-card class="growth-card mb-3" v-for="campaign in activeCampaigns" :key="campaign.id">
        <f7-card-header>
          <div class="display-flex align-items-center">
            <f7-icon f7="star_fill" size="24" class="t-gold mr-2"></f7-icon>
            <div class="t-bold">{{ campaign.title }}</div>
          </div>
        </f7-card-header>
        <f7-card-content>
          <div class="t-small mb-2">{{ campaign.description }}</div>

          <div class="stats-row mb-2">
            <div class="stat">
              <div class=" t-small">{{ $t('growth.campaigns.budget') }}</div>
              <div class="t-h5 t-bold">{{ formatMoney(campaign.budget) }}</div>
            </div>
            <div class="stat">
              <div class=" t-small">{{ $t('growth.campaigns.distributed') }}</div>
              <div class="t-h5 t-bold">{{ formatMoney(campaign.distributed) }}</div>
            </div>
            <div class="stat">
              <div class="t-small">{{ $t('growth.campaigns.remaining') }}</div>
              <div class="t-h5 t-bold">{{ formatMoney(campaign.remaining) }}</div>
            </div>
          </div>

          <f7-progressbar
            :progress="calculateProgress(campaign.distributed, campaign.budget)"
            class="progress-orange"
          ></f7-progressbar>

          <div class="display-flex justify-content-space-between mt-2 t-xs ">
            <div>{{ calculateProgress(campaign.distributed, campaign.budget) }}{{ $t('growth.campaigns.pctUsed') }}</div>
            <div>{{ campaign.startDate }} - {{ campaign.endDate }}</div>
          </div>
        </f7-card-content>
        <f7-card-footer>
          <div class="display-flex justify-content-space-between">
            <f7-button small raised round  fill class="btn-blue" @click="viewRules(campaign)">{{ $t('growth.campaigns.rules') }}</f7-button>
            <f7-button small raised round  fill class="btn-green" @click="editCampaign(campaign)">{{ $t('growth.campaigns.edit') }}</f7-button>
            <f7-button small raised round  fill class="btn-terracotta" @click="pauseCampaign(campaign)">{{ $t('growth.campaigns.pause') }}</f7-button>
          </div>
        </f7-card-footer>
      </f7-card>

      <!-- Upcoming Campaigns -->
      <f7-block-title class="t-bold">{{ $t('growth.campaigns.upcomingCampaigns') }}</f7-block-title>
      <f7-card class="growth-card mb-3" v-for="campaign in upcomingCampaigns" :key="campaign.id">
        <f7-card-header>
          <div class="display-flex align-items-center">
            <f7-icon f7="calendar" size="24" class="t-blue mr-2"></f7-icon>
            <div class="t-bold">{{ campaign.title }}</div>
          </div>
        </f7-card-header>
        <f7-card-content>
          <div class=" t-small mb-2">{{ campaign.description }}</div>

          <div class="stats-row mb-2">
            <div class="stat">
              <div class=" t-small">{{ $t('growth.campaigns.budget') }}</div>
              <div class="t-h5 t-bold">{{ formatMoney(campaign.budget) }}</div>
            </div>
            <div class="stat">
              <div class=" t-small">{{ $t('growth.campaigns.startDate') }}</div>
              <div class="t-small">{{ campaign.startDate }}</div>
            </div>
          </div>

          <f7-progressbar :progress="0" class="progress-blue"></f7-progressbar>
        </f7-card-content>
        <f7-card-footer>
          <div class="display-flex justify-content-space-between">
            <f7-button small raised round  fill class="btn-blue" @click="viewRules(campaign)">{{ $t('growth.campaigns.rules') }}</f7-button>
            <f7-button small raised round  fill class="btn-green" @click="editCampaign(campaign)">{{ $t('growth.campaigns.edit') }}</f7-button>
            <f7-button small raised round  fill class="btn-gold" @click="activateCampaign(campaign)">{{ $t('growth.campaigns.activate') }}</f7-button>
          </div>
        </f7-card-footer>
      </f7-card>

      <!-- Past Campaigns -->
      <f7-block-title class="t-bold">{{ $t('growth.campaigns.pastCampaigns') }}</f7-block-title>
      <f7-card class="growth-card mb-3" v-for="campaign in pastCampaigns" :key="campaign.id">
        <f7-card-header>
          <div class="display-flex align-items-center">
            <f7-icon f7="checkmark_circle" size="24" class="t-green mr-2"></f7-icon>
            <div class="t-bold">{{ campaign.title }}</div>
          </div>
        </f7-card-header>
        <f7-card-content>
          <div class=" t-small mb-2">{{ campaign.description }}</div>

          <div class="stats-row">
            <div class="stat">
              <div class=" t-small">{{ $t('growth.campaigns.budget') }}</div>
              <div class="t-small">{{ formatMoney(campaign.budget) }}</div>
            </div>
            <div class="stat">
              <div class="t-small">{{ $t('growth.campaigns.distributed') }}</div>
              <div class="t-small">{{ formatMoney(campaign.distributed) }}</div>
            </div>
            <div class="stat">
              <div class="t-small">{{ $t('growth.campaigns.status') }}</div>
              <div class="t-small t-green">{{ campaign.status }}</div>
            </div>
          </div>
        </f7-card-content>
        <f7-card-footer>
          <f7-button small raised round  fill color="gray" @click="viewRules(campaign)">{{ $t('growth.campaigns.viewRules') }}</f7-button>
        </f7-card-footer>
      </f7-card>
    </f7-block>
       </template>
</AppLayout>
    <!-- Create/Edit Campaign Modal -->
    <AdaptiveSheet class="campaign-modal"  :close-by-backdrop-click="true" :close-on-escape="true" :swipe-to-close="true">
      <f7-page>
        <f7-navbar :title="$t('growth.campaigns.modalTitle')" >
          <f7-nav-right><f7-link right sheet-close popup-close><f7-icon ios="f7:multiply" md="material:close" /></f7-link></f7-nav-right>
        </f7-navbar>
        <f7-block>
          <f7-list form inset strong outline>
            <f7-list-input
              v-model:value:value="formData.title"
              type="text"
              :label="$t('growth.campaigns.campaignTitle')"
              :placeholder="$t('growth.campaigns.exampleTitle')"
              required
            ></f7-list-input>

            <f7-list-input
              v-model:value="formData.description"
              type="textarea"
              :label="$t('growth.campaigns.description')"
              :placeholder="$t('growth.campaigns.descriptionPlaceholder')"
              required
            ></f7-list-input>

            <f7-list-input
              v-model:value.number="formData.budget"
              type="number"
              :label="$t('growth.campaigns.budgetXOF')"
              :placeholder="$t('growth.campaigns.exampleBudget')"
              required
            ></f7-list-input>

            <f7-list-input
              v-model:value="formData.startDate"
              type="date"
              :label="$t('growth.campaigns.startDate')"
              required
            ></f7-list-input>

            <f7-list-input
              v-model:value="formData.endDate"
              type="date"
              :label="$t('growth.campaigns.endDate')"
              required
            ></f7-list-input>

            <f7-list-item
              :title="$t('growth.campaigns.rewardType')"
              smart-select
              :smart-select-params="{ openIn: isSmallScreen? 'sheet':'popup', closeOnSelect: true, sheetSwipeToClose: true }"
            >
              <select name="rewardType" v-model="formData.rewardType" required>
                <option value="GROWTH_CREDIT">{{ $t('growth.campaigns.growthCredits') }}</option>
                <option value="TRUST_POINT">{{ $t('growth.campaigns.trustPoints') }}</option>
                <option value="CASH">{{ $t('growth.campaigns.cashWallet') }}</option>
                <option value="BADGE">{{ $t('growth.campaigns.badgeAchievement') }}</option>
                <option value="MARKETPLACE_CREDIT">{{ $t('growth.campaigns.marketplaceCredit') }}</option>
              </select>
            </f7-list-item>

            <f7-list-item
              :title="$t('growth.campaigns.status')"
              smart-select
              :smart-select-params="{ openIn:isSmallScreen? 'sheet':'popup', closeOnSelect: true, sheetSwipeToClose: true }"
            >
              <select name="campaignStatus" v-model="formData.status" required>
                <option value="DRAFT">{{ $t('growth.campaigns.draft') }}</option>
                <option value="ACTIVE">{{ $t('growth.campaigns.active') }}</option>
                <option value="PAUSED">{{ $t('growth.campaigns.paused') }}</option>
                <option value="COMPLETED">{{ $t('growth.campaigns.completed') }}</option>
              </select>
            </f7-list-item>
          </f7-list>

          <f7-block class="mt-4">
            <f7-button large raised round  fill class="btn-green" @click="saveCampaign" block>
              {{ $t('growth.campaigns.saveCampaign') }}
            </f7-button>
          </f7-block>
        </f7-block>
      </f7-page>
    </AdaptiveSheet>
  </f7-page>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import {
  f7Page, f7Navbar, f7Toolbar, f7Link, f7Icon, f7Block, f7Card, f7CardHeader,
  f7CardContent, f7CardFooter, f7BlockTitle, f7List, f7ListItem, f7ListInput,
  f7Progressbar, f7Button, f7Popup
} from 'framework7-vue'
import { f7 } from 'framework7-vue'
import { useGrowthStore } from '../js/pinia-stores/growth.js'
import AdaptiveSheet from '../components/common/AdaptiveSheet.vue'
import { openAdaptiveModal, closeAdaptiveModal } from '../js/composables/useAdaptiveModal.js'
import { useSmallScreen } from '../js/composables/useMediaQuery.js'

import i18n from '../js/i18n.js'
const { t } = i18n.global

const growthStore = useGrowthStore()
const isSmallScreen = useSmallScreen().matches
const campaigns = computed(() => growthStore.campaigns)
const editingCampaign = ref(null)

const formData = ref({
  id: null,
  title: '',
  description: '',
  budget: 0,
  startDate: '',
  endDate: '',
  rewardType: 'GROWTH_CREDIT',
  status: 'DRAFT',
})

const activeCampaigns = computed(() => campaigns.value.filter((c) => c.status === 'ACTIVE'))
const upcomingCampaigns = computed(() => campaigns.value.filter((c) => c.status === 'DRAFT'))
const pastCampaigns = computed(() => campaigns.value.filter((c) =>
  c.status === 'COMPLETED' || c.status === 'PAUSED'
))

const totalBudget = computed(() => {
  const sum = campaigns.value.reduce((acc, c) => acc + Number(c.budget || 0), 0)
  return Math.round(sum / 1_000_000)
})

const totalDistributed = computed(() => {
  const sum = campaigns.value.reduce((acc, c) => acc + Number(c.distributed || 0), 0)
  return Math.round(sum / 1_000_000)
})

onMounted(async () => {
  await loadCampaigns()
})

async function loadCampaigns() {
  try {
    await growthStore.fetchRewardCampaigns()
  } catch (e) {
    f7.dialog.alert(t('growth.campaigns.errorLoading', { message: e.message }))
  }
}

function formatMoney(amount) {
  if (!amount && amount !== 0) return '0'
  return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0 }).format(amount) + ' XOF'
}

function calculateProgress(distributed, budget) {
  if (!budget || budget <= 0) return 0
  return Math.min(100, Math.round((Number(distributed || 0) / Number(budget)) * 100))
}

function openCreateCampaign() {
  editingCampaign.value = null
  formData.value = {
    id: null,
    title: '',
    description: '',
    budget: 0,
    startDate: '',
    endDate: '',
    rewardType: 'GROWTH_CREDIT',
    status: 'DRAFT',
  }
  openAdaptiveModal('.campaign-modal')
}

function editCampaign(campaign) {
  editingCampaign.value = campaign
  formData.value = {
    id: campaign.id,
    title: campaign.title,
    description: campaign.description,
    budget: campaign.budget,
    startDate: campaign.startDate,
    endDate: campaign.endDate,
    rewardType: campaign.rewardType,
    status: campaign.status,
  }
  openAdaptiveModal('.campaign-modal')
}

async function saveCampaign() {
  if (!formData.value.title || !formData.value.budget || !formData.value.startDate || !formData.value.endDate) {
    f7.dialog.alert(t('growth.campaigns.fillRequired'))
    return
  }

  try {
    if (editingCampaign.value && formData.value.id) {
      await growthStore.updateCampaign(formData.value.id, formData.value)
      f7.toast.show({ text: t('growth.campaigns.campaignUpdated'), position: 'bottom' })
    } else {
      await growthStore.createCampaign(formData.value)
      f7.toast.show({ text: t('growth.campaigns.campaignCreated'), position: 'bottom' })
    }

    closeAdaptiveModal('.campaign-modal')
  } catch (error) {
    f7.dialog.alert(t('growth.campaigns.errorSaving', { message: error.message }))
  }
}

function pauseCampaign(campaign) {
  f7.dialog.confirm(
    t('growth.campaigns.confirmPause'),
    t('growth.campaigns.pauseCampaign'),
    async () => {
      try {
        await growthStore.pauseCampaign(campaign.id)
        f7.toast.show({ text: t('growth.campaigns.campaignPaused'), position: 'bottom' })
      } catch (error) {
        f7.dialog.alert(t('growth.campaigns.errorPausing', { message: error.message }))
      }
    }
  )
}

function activateCampaign(campaign) {
  f7.dialog.confirm(
    t('growth.campaigns.confirmActivate'),
    t('growth.campaigns.activateCampaign'),
    async () => {
      try {
        await growthStore.activateCampaign(campaign.id)
        f7.toast.show({ text: t('growth.campaigns.campaignActivated'), position: 'bottom' })
      } catch (error) {
        f7.dialog.alert(t('growth.campaigns.errorActivating', { message: error.message }))
      }
    }
  )
}

function viewRules(campaign) {
  f7.views.main.router.navigate({
    url: '/growth/admin/rewards/',
    props: { campaignId: campaign.id },
  })
}
</script>

<style scoped>
.growth-page {
  --avo-green: var(--f7-theme-color, #1a6b42);
  --avo-green-light: var(--avocash-green-light, #eaf2eb);
  --avo-gold: var(--f7-secondary, #c9a84c);
  --avo-gold-light: var(--avocash-gold-light, #fdf6e3);
  --avo-blue: var(--f7-blue, #3b4fca);
  --avo-blue-light: var(--avocash-blue-light, #ebf0fc);
  --avo-terracotta: var(--f7-error, #ba1a1a);
  --avo-terracotta-light: var(--avocash-terracotta-light, #fdecea);
  --avo-leaf: var(--f7-theme-color, #5f8a4a);
  --avo-purple: var(--f7-purple, #6b46c1);
  --avo-purple-light: var(--avocash-purple-light, #f5f0ff);
  --avo-surface: var(--avocash-surface, #f4f7f2);
  --avo-pit: var(--f7-on-surface, #1c1c1e);
}

.t-bold { font-weight: 600; }
.t-h4 { font-size: 18px; font-weight: 600; }
.t-h5 { font-size: 15px; font-weight: 600; }
.t-small { font-size: 12px; }
.t-xs { font-size: 11px; }
.t-muted { color: var(--f7-on-surface-variant, #6f7a71); }

.t-green { color: var(--avo-green); }
.t-gold { color: var(--avo-gold); }
.t-blue { color: var(--avo-blue); }
.t-terracotta { color: var(--avo-terracotta); }

.growth-card { border-radius: 14px; }
.growth-card :deep(.card-header) { font-size: 15px; gap: 8px; }

.stats-row { display: flex; justify-content: space-between; gap: 8px; }
.stat { flex: 1; }

.progress-orange :deep(.progressbar-inner) { background: var(--avo-terracotta); }
.progress-blue :deep(.progressbar-inner) { background: var(--avo-blue); }

.btn-green { --f7-button-bg-color: var(--avo-green); --f7-button-text-color: var(--f7-on-primary, #fff); }
.btn-blue { --f7-button-bg-color: var(--avo-blue); --f7-button-text-color: var(--f7-on-primary, #fff); }
.btn-gold { --f7-button-bg-color: var(--avo-gold); --f7-button-text-color: var(--f7-on-primary, #fff); }
.btn-terracotta { --f7-button-bg-color: var(--avo-terracotta); --f7-button-text-color: var(--f7-on-primary, #fff); }
.btn-green.active-state, .btn-blue.active-state,
.btn-gold.active-state, .btn-terracotta.active-state { opacity: .8; color: #fff;}
</style>
